import { buy, payssionBuy } from "@/apis/pricing"
import { pricingGoogsQueryOption } from "@/apis/query/pricing"
import { useCoinBalance } from "@/hooks/useCoinBalance"
import { useToast } from "@/provider/ToastProvider"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { Energy } from "@/ui/icons/Energy"
import { Button, cn, Image, useDisclosure } from "@heroui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { motion } from "framer-motion"
import { useLocale, useTranslations } from "next-intl"
import numeral from "numeral"
import { useEffect, useMemo, useRef, useState } from "react"
import { Pending } from "../Pending"
import useGoodsList from "./useGoodsList"
import { useShowPricing } from "."
import GlobalLoading from "../GlobalLoading"
import PayMethodModal from "./PayMethodModal"
import RedeemEntry from "./RedeemEntry"
import { savePayRedirect } from "@/util/payRedirect"

type CurrencyTab = 'energy' | 'crystals'

export default ({ initialTab = 'energy' }: { initialTab?: CurrencyTab }) => {
  const t = useTranslations()
  const [avatar, name] = useUserDetailStore(state => [state.avatar, state.name])
  const { coin, starCoin } = useCoinBalance()
  const { data, isPending } = useQuery(pricingGoogsQueryOption(2))
  const locale = useLocale()

  // goods[0] = 能量商品，goods[1] = 新增币种(水晶)商品
  const energyGoods = useMemo(() => data?.data.goods[0], [data])
  const crystalGoods = useMemo(() => data?.data.goods[1], [data])

  const energyProducts = useGoodsList(energyGoods || {})
  const crystalProducts = useGoodsList(crystalGoods || {})

  const [tab, setTab] = useState<CurrencyTab>(initialTab)
  const hasCrystals = !!crystalGoods
  const products = tab === 'crystals' ? crystalProducts : energyProducts

  useEffect(() => {
    if (!isPending && !hasCrystals && tab === 'crystals') {
      setTab('energy')
    }
  }, [hasCrystals, isPending, tab])

  const buyMuta = useMutation({
    mutationFn: buy,
    onSuccess(res) {
      if (res.status === 200) {
        window.open(res.data.link, "_self")
      } else {
        showToast({ type: "error", message: res.message })
      }
      setIsBuyPending(false)
    },
    onError() {
      setIsBuyPending(false)
    }
  })

  const payssionBuyMuta = useMutation({
    mutationFn: payssionBuy,
    onSuccess(res) {
      if (res.status === 200) {
        window.open(res.data.link, "_self")
      } else {
        showToast({ type: "error", message: res.message })
      }
    },
  })

  const { showToast } = useToast()
  const showPricing = useShowPricing()


  const [isBuyPending, setIsBuyPending] = useState(false)

  // 俄罗斯语言(选择支付方式)
  const payMethodModal = useDisclosure()
  const goodsId = useRef<number>(null!)

  const onBuy = (id) => {
    savePayRedirect()
    if (locale === 'ru') {
      // 俄罗斯支付
      payMethodModal.onOpen()
      goodsId.current = id
      return
    }

    setIsBuyPending(true)
    buyMuta.mutate({ goods_id: id },)
  }


  const onPressPaypal = () => {
    savePayRedirect()
    buyMuta.mutate({ goods_id: goodsId.current })

  }
  const onPressPayssion = () => {
    savePayRedirect()
    payssionBuyMuta.mutate({ goods_id: goodsId.current })
  }

  return (<>
    {
      isBuyPending
        ? <GlobalLoading />
        : null
    }
    <div className="max-w-[680px] w-full h-full overflow-hidden flex flex-col md:block">
      <header className="flex relative justify-between shrink-0 bg-cover bg-no-repeat bg-[url('/imgs/energy-pricing-header.png')] h-[120px] items-center px-4 sm:px-8">
        <div className="flex gap-3 items-center">
          <Image radius="full" classNames={{ wrapper: "border-1 flex-shrink-0 border-theme-white ", img: "w-14 h-14 sm:w-16 sm:h-16" }} src={avatar} />
          <div>
            <p className="text-theme-white text-[20px]">{name}</p>
            <span className="mt-[6px] inline-flex h-8 items-center gap-2 rounded-full border-[0.5px] border-theme-white/20 bg-theme-white/[0.06] px-3 text-[14px] font-bold text-theme-white">
              <Energy width={16} height={16} />
              <span className="leading-none">{coin}</span>
              <span className="h-3 w-px bg-theme-white/20" />
              <Image removeWrapper src="/imgs/creationAiCoin.png" className="w-4 h-4" />
              <span className="leading-none">{starCoin}</span>
            </span>
          </div>
        </div>
        <Button className="bg-transparent absolute right-4 bottom-4 sm:relative text-[12px] sm:text-[16px] text-[#FFE49D]" size="sm" disableRipple onPress={() => showPricing('premium')}>Buy Premium</Button>
      </header>

      <div className="pt-8 sm:pt-10 pb-4 lg:pb-16 sm:pb-28 bg-surface sm:min-h-[524px] overflow-hidden flex-1 flex flex-col lg:block">
        {
          isPending ?
            <Pending /> :
            <>
              {
                hasCrystals &&
                <div className="px-4 sm:px-20 mb-2 sm:mb-8 shrink-0">
                  <div className="relative flex h-11 items-center rounded-full bg-theme-primary/5 p-[2px] text-center text-[16px] font-bold">
                    {(['energy', 'crystals'] as CurrencyTab[]).map(key => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setTab(key)}
                        className={cn(
                          "relative z-10 flex flex-1 items-center justify-center gap-2 h-full rounded-full transition-colors",
                          tab === key ? "text-theme-primary" : "text-theme-primary/70"
                        )}
                      >
                        {key === 'energy' ? <Energy width={16} height={16} /> : <Image removeWrapper src="/imgs/creationAiCoin.png" className="w-4 h-4" />}
                        <span>{key === 'energy' ? t('common.energy') : t('common.crystals')}</span>
                        {
                          tab === key &&
                          <motion.span
                            layoutId="energy-pricing-tab-indicator"
                            className="absolute inset-0 z-[-1] rounded-full border-[0.5px] border-theme-primary/20 bg-surface shadow-sm"
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        }
                      </button>
                    ))}
                  </div>
                </div>
              }

              <ul className="px-4 sm:px-20 bg-surface py-2 flex flex-col gap-3 flex-1 overflow-y-auto">
                {
                  products.map(item => (
                    <li key={item.id} className="px-2 sm:px-7 py-3 sm:py-[18px] w-full flex items-center justify-between rounded-[12px] border-1 border-theme-primary/20">
                      <span className="flex gap-2 items-center">
                        {tab === 'crystals' ? <Image removeWrapper src="/imgs/creationAiCoin.png" className="w-9 h-9" /> : <Energy width={36} height={36} />}
                        <span className="font-bold text-[20px]">{item.billingPeriod}</span>
                        {
                          item.firstRechargeReward &&
                          <span className="text-[14px] font-bold text-theme-primary/40">+{item.firstRechargeReward}</span>
                        }
                      </span>

                      {
                        (item.firstRechargeRate || item.rebate) &&
                        <span className="flex items-center gap-1.5 sm:gap-2">
                          {
                            item.firstRechargeRate &&
                            <span className={cn(
                              "text-[13px] font-normal text-center leading-6 h-6 inline-block min-w-[56px] px-2 rounded-full",
                              "bg-gradient-to-r from-[#EAF890] to-[#60F2D0] text-[#341537]"
                            )}>
                              +{numeral(item.firstRechargeRate).format('0%')}
                            </span>
                          }
                          {
                            item.rebate &&
                            <motion.span
                              animate={{
                                scale: [1, 1.2, .9, 1],
                              }}
                              className={cn(
                                "text-[13px] font-normal text-center leading-6 h-6 inline-block text-theme-white min-w-[64px] rounded-full",
                                "bg-gradient-to-l from-gradient-purple2 to-gradient-blue2 "
                              )}>
                              {numeral(item.rebate).format('0%')}off
                            </motion.span>
                          }
                        </span>
                      }

                      <Button radius="full" className="bg-[#FDDE6F] text-[#936312] font-bold text-[14px] leading-[1]"
                        onPress={
                          () => onBuy(item.id)
                        }>{item.unit} {item.pricing}</Button>

                    </li>
                  ))
                }


              </ul>
              <RedeemEntry className="mt-4 px-4 shrink-0" />
            </>
        }
      </div>

    </div>

    <PayMethodModal
      isOpen={payMethodModal.isOpen}
      onOpenChange={payMethodModal.onOpenChange}
      isPaypalLoading={buyMuta.isPending}
      isPayssionLoading={payssionBuyMuta.isPending}
      onPressPaypal={onPressPaypal}
      onPressPayssion={onPressPayssion} />

  </>)
}
